import { useState, type ReactNode } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { validateRedirect } from "@njbsoft/utils";
import { authApiClient } from "@/lib/auth";
import { router } from "@/router";
import { useCompanyStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import {
  testerInviteLookupErrorKey,
  useTesterInviteLookup,
} from "@/hooks/use-tester-invite-lookup";
import { testerInviteCtaTargets } from "@/lib/tester-invite-links";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOKEN_RE = /^[a-f0-9]{64}$/;

// Every control is a 44 px target below `sm`; from `sm`, its own height.
const INPUT_CLASS =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface max-sm:min-h-11";
const LABEL_CLASS = "block text-sm font-medium text-muted";
const CTA_PRIMARY =
  "inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-hover max-sm:min-h-11";
const SUBMIT_CLASS =
  "w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 max-sm:min-h-11";
const TEXT_LINK_CLASS =
  "font-medium text-primary hover:text-primary-hover max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center max-sm:justify-center";

interface SignupSearch {
  token?: string;
  redirect?: string;
}

/** Where a visitor who turns out to already have an account is sent. */
interface SignInTarget {
  to: string;
  search: { redirect?: string };
}

export const Route = createFileRoute("/signup")({
  validateSearch: (search: Record<string, unknown>): SignupSearch => ({
    // The token is a 32-byte hex credential. A value of any other shape never
    // reaches the wire: it cannot be a real token, so posting it would only
    // spend a rate-limit slot to be told so.
    token:
      typeof search.token === "string" && TOKEN_RE.test(search.token) ? search.token : undefined,
    // Raw here, judged by `validateRedirect` at use — the same split `/login`
    // keeps, so one validator owns the open-redirect rule for both routes.
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: ({ context }) => {
    // The persisted persona snapshot and pinned offline identity belong to the
    // session already open. Letting a second identity overwrite the token
    // beneath them resolves the new account against the old one's personas, so
    // signing out is the only way onto this route with a session in hand —
    // `clearCompanySession` retires both.
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/" });
    }
  },
  component: SignupPage,
});

function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation("auth");
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <h2 className="text-center text-3xl font-bold text-on-surface">
          {t("createAccountTitle")}
        </h2>
        {children}
      </div>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-danger">{message}</p>;
}

/**
 * The exit for a visitor whose address is already registered. It is reached two
 * ways — the lookup's `account_exists`, and a 409 from the signup itself — and
 * both need the same thing: the sign-in route carrying the redirect this visit
 * came in with, because on the invite-bound arm the address cannot be edited.
 */
function AlreadyRegistered({ target }: { target: SignInTarget }) {
  const { t } = useTranslation(["auth", "company"]);
  return (
    <div className="space-y-4 text-center">
      <p className="text-base font-semibold text-on-surface">
        {t("company:testerSignup.alreadyRegisteredTitle")}
      </p>
      <p className="text-sm text-muted">{t("company:testerSignup.alreadyRegisteredBody")}</p>
      <Link to={target.to} search={target.search} className={CTA_PRIMARY}>
        {t("auth:signInLink")}
      </Link>
    </div>
  );
}

function SignInFooter({ target }: { target: SignInTarget }) {
  const { t } = useTranslation("auth");
  return (
    <p className="text-center text-sm text-muted">
      {t("hasAccount")}{" "}
      <Link to={target.to} search={target.search} className={TEXT_LINK_CLASS}>
        {t("signInLink")}
      </Link>
    </p>
  );
}

/**
 * Account creation for the company portal, public and invite-aware.
 *
 * A tester invitation is the reason this route exists: the mailed landing can
 * name who invited whom before the visitor has any account, and the only way to
 * act on that is to create one. When the visit carries a token, the invited
 * address is the account's address — resolved from the lookup and submitted
 * from there, never from the field, because a read-only input is a UI
 * affordance and not a gate. A token that cannot be resolved renders its
 * refusal and no form at all: a free-text signup would create an account that
 * can never accept the invitation it was made for.
 *
 * The MFA prompt `apps/web` shows after signup has no counterpart here. A
 * brand-new account holds no tester authorization yet, so there is no utility
 * whose policy could demand enrolment; that demand lands on `/settings`
 * once a grant exists.
 *
 * This route is the wizard's first step: on success the visitor lands on the
 * portal gate, where the signup wizard stands open on its Company step —
 * account done, everything else still to collect.
 */
function SignupPage() {
  const { t } = useTranslation(["auth", "company", "common"]);
  const navigate = useNavigate();
  const { token, redirect: redirectParam } = Route.useSearch();
  const safeRedirect = validateRedirect(redirectParam);
  const setAuth = useCompanyStore((s) => s.setAuth);
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [emailConflict, setEmailConflict] = useState(false);
  const lookup = useTesterInviteLookup(token);

  const invite = lookup.isError ? undefined : lookup.data;
  // The address the account MUST be created under, or null when the visitor
  // arrived without an invitation and may type their own.
  const boundEmail = invite?.email ?? null;

  // Both "you already have an account" exits point where the landing's own
  // sign-in CTA points, built by the one helper that owns that URL's shape.
  const signInTarget: SignInTarget =
    token === undefined
      ? { to: "/login", search: safeRedirect === null ? {} : { redirect: safeRedirect } }
      : testerInviteCtaTargets(token).signIn;

  const signupMutation = useMutation({
    mutationFn: async (creds: { full_name: string; email: string; password: string }) => {
      // The audience is this portal's own identity, not the visitor's input:
      // it lands on the profile and decides which origin the verification
      // mail — and its bodyless resend — links back to.
      const { data, error, response } = await authApiClient.POST("/auth/signup", {
        body: { ...creds, audience: "company" },
      });
      if (error) {
        const envelope = error as { error?: string; code?: string };
        throw new ApiError(
          response.status,
          envelope.error ?? "Failed to create the account",
          envelope.code,
          error,
        );
      }
      return data;
    },
    onSuccess: async (data) => {
      setAuth(
        data.access_token,
        {
          id: data.user.id,
          email: data.user.email,
          role: data.user.role,
          emailVerified: data.user.email_verified,
        },
        data.refresh_token,
      );
      useCompanyStore.getState().setExpiresAt(new Date(data.expires_at).getTime());
      // The wire response nests preferences under `user`. A just-created
      // profile normally carries none, so these branches are expected to no-op.
      const prefs = data.user.preferences;
      if (prefs) {
        const theme = prefs.theme;
        if (theme === "system" || theme === "light" || theme === "dark") {
          useCompanyStore.getState().setThemePreference(theme);
        }
        const locale = prefs.locale;
        if (locale === "system" || locale === "en" || locale === "es") {
          useCompanyStore.getState().setLocalePreference(locale);
        }
        if (typeof prefs.accent_color === "string" && prefs.accent_color) {
          useCompanyStore.getState().setAccentColor(prefs.accent_color);
        }
      }
      // A brand-new identity must resolve from its own probes, never from
      // whatever this browser last persisted for whoever signed in before.
      useCompanyStore.getState().setPersonaSnapshot(null);
      useCompanyStore.getState().setOfflineIdentity(null);
      await router.invalidate();
      void navigate({ to: safeRedirect ?? "/" });
    },
    onError: (err: unknown) => {
      // The residual race the lookup cannot close: a profile created for the
      // invited address after the lookup answered. The address is not editable
      // on the invite-bound arm, so sign-in is the only correct exit.
      if (err instanceof ApiError && err.status === 409) {
        setEmailConflict(true);
        return;
      }
      showApiError((err as { code?: string }).code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const full_name = (formData.get("full_name") as string).trim();
    // The invited address wins over whatever the field holds: only that address
    // can accept the invitation the account is being created for.
    const email = boundEmail ?? (formData.get("email") as string).trim();
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirm_password") as string;

    const errors: Record<string, string> = {};
    if (!full_name) {
      errors.full_name = t("common:validation.required");
    }
    if (!email) {
      errors.email = t("common:validation.required");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("common:validation.invalidEmail");
    }
    if (!password) {
      errors.password = t("common:validation.required");
    } else if (password.length < 8) {
      errors.password = t("common:validation.minLength", { min: 8 });
    }
    if (!confirmPassword) {
      errors.confirm_password = t("common:validation.required");
    } else if (password && confirmPassword !== password) {
      errors.confirm_password = t("passwordMismatch");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setEmailConflict(false);
    signupMutation.mutate({ full_name, email, password });
  };

  if (token !== undefined && lookup.isError) {
    return (
      <Shell>
        <p className="text-center text-sm text-danger">
          {t(testerInviteLookupErrorKey(lookup.error))}
        </p>
        <SignInFooter target={signInTarget} />
      </Shell>
    );
  }

  if (token !== undefined && invite === undefined) {
    return (
      <Shell>
        <div className="space-y-4 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted">{t("company:testerInviteLookup.loading")}</p>
        </div>
      </Shell>
    );
  }

  if (invite?.account_exists) {
    return (
      <Shell>
        <AlreadyRegistered target={signInTarget} />
      </Shell>
    );
  }

  return (
    <Shell>
      {invite && (
        <p className="text-sm text-on-surface">
          {t("company:testerSignup.invitedBody", { orgName: invite.org_name })}
        </p>
      )}
      {/* The visitor without an invitation is on this route to open a company
          workspace: their next screen after the account exists is the signup
          wizard the portal gate opens on. An invited visitor is a tester
          acceptances flow — the sentence would be wrong for them. */}
      {!invite && <p className="text-sm text-muted">{t("company:testerSignup.companyNext")}</p>}
      {emailConflict && <AlreadyRegistered target={signInTarget} />}
      <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          <div>
            <label htmlFor="full_name" className={LABEL_CLASS}>
              {t("fullNameLabel")}
            </label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              autoComplete="name"
              className={INPUT_CLASS}
            />
            <FieldError message={fieldErrors.full_name} />
          </div>
          <div>
            <label htmlFor="email" className={LABEL_CLASS}>
              {t("emailLabel")}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className={INPUT_CLASS}
              {...(boundEmail === null ? {} : { readOnly: true, value: boundEmail })}
            />
            {boundEmail !== null && (
              <p className="mt-1 text-xs text-muted">{t("company:testerSignup.emailLocked")}</p>
            )}
            <FieldError message={fieldErrors.email} />
          </div>
          <div>
            <label htmlFor="password" className={LABEL_CLASS}>
              {t("passwordLabel")}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
            <p className="mt-1 text-xs text-muted">{t("passwordMinLength")}</p>
            <FieldError message={fieldErrors.password} />
          </div>
          <div>
            <label htmlFor="confirm_password" className={LABEL_CLASS}>
              {t("confirmPasswordLabel")}
            </label>
            <input
              id="confirm_password"
              name="confirm_password"
              type="password"
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
            <FieldError message={fieldErrors.confirm_password} />
          </div>
        </div>

        <button type="submit" disabled={signupMutation.isPending} className={SUBMIT_CLASS}>
          {signupMutation.isPending ? t("creatingAccount") : t("createAccount")}
        </button>
      </form>

      <SignInFooter target={signInTarget} />
    </Shell>
  );
}
